import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { API, Auth } from 'aws-amplify';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { cookieAttributes } from 'util/resumeCookieSettings';
import classes from './QueryDetails.module.scss';
import { buildQueryBreadcrumbs, getToken, reportHeading, useOpenQueryBridge } from 'features/queryReports/utils/commonFunctions';
import { Breadcrumbs } from 'features/queryReports/breadcrumb/breadcrumbs';
import { useAuthReady } from 'features/queryReports/hooks/useAuthReady';
import {
  marketShareIDs,
  clinicalMarketShareIDs,
  populationTrackerIDs,
  clinicalVolumesIDs,
  trendedPOSIDs,
  placeOfServiceByCountyIDs,
  placeOfServiceIDs,
  billingReportIDs,
  countyLevelServicesIDs,
  shareOfCareIDs,
  campaignEffectivenessIDs
} from 'features/queryReports/utils/reportIds';

const MarketShare = lazy(() =>
  import('./reports/MarketShare').catch(() => ({
    default: () => <div>Error loading Market Share Report</div>
  }))
);
const ClinicalMarketShare = lazy(() =>
  import('./reports/ClinicalMarketShare').catch(() => ({
    default: () => <div>Error loading Clinical Market Share Report</div>
  }))
);
const PopulationTracker = lazy(() =>
  import('./reports/PopulationTracker').catch(() => ({
    default: () => <div>Error loading Population Tracker Report</div>
  }))
);
const ClinicalVolumes = lazy(() =>
  import('./reports/ClinicalVolumes').catch(() => ({
    default: () => <div>Error loading Clinical Volumes Report</div>
  }))
);
const TrendedPOS = lazy(() =>
  import('./reports/TrendedPOS').catch(() => ({
    default: () => <div>Error loading Trended Place of Service Report</div>
  }))
);
const PlaceOfServiceByCounty  = lazy(() =>
  import('./reports/PlaceOfServiceByCounty').catch(() => ({
    default: () => <div>Error loading Place of Service by County Report</div>
 }))
);
const PlaceOfService = lazy(() =>
  import('./reports/PlaceOfService').catch(() => ({
    default: () => <div>Error loading Place of Service Report</div>
  }))
);
const BillingReport = lazy(() =>
  import('./reports/BillingReport').catch(() => ({
    default: () => <div>Error loading Billing Report</div>
  }))
);
const CountyLevelServicesReport = lazy(() =>
  import('./reports/CountyLevelServices').catch(() => ({
    default: () => <div>Error loading County Level Services Report</div>
  }))
);
const ShareOfCare = lazy(() =>
  import('./reports/ShareOfCare').catch(() => ({
    default: () => <div>Error loading Share of Care Report</div>
  }))
);
const CampaignEffectiveness = lazy(() =>
  import('./reports/CampaignEffectiveness').catch(() => ({
    default: () => <div>Error loading Campaign Effectiveness Report</div>
  }))
);

function QueryDetails() {
  useEffect(() => {
    async function setResumeCookie() {
      const cookieValue = encodeURIComponent(`QUERY - /openquery/commercial`);
      const resumeCookie = Object.assign(cookieAttributes(), { name: 'platform_resume' }, { value: cookieValue });
      await cookieStore.set(resumeCookie);
    }
    setResumeCookie();
  }, []);

  const [selectedReport, setSelectedReport] = useState([]);
  const reportsInFlight = useRef(false);
  const location = useLocation();
  const showOpenMyQueries = location.pathname !== "/openquery/queries";
  const history = useHistory();
  const { payerMix, queryId } = useParams();
  const bridge = useOpenQueryBridge({ history, payerMix, getToken });
  // --- Auth readiness ---
  const ready = useAuthReady();

  // --- Fetch reports only when authenticated ---
  useEffect(() => {
    if (!ready) return;
    if (reportsInFlight.current) return;
    reportsInFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check
        const reports = await API.get('OpenQueryAPI', `/openquery/reports`);
        if (cancelled) return;
        const selectedReport = reports.reports.category.find(item => item.id === queryId);
        if (selectedReport) setSelectedReport(selectedReport);
      } catch (err) {
        console.error('error fetching openquery reports', err);
      } finally {
        reportsInFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready]);

  const items = buildQueryBreadcrumbs({
    payerMix,
    selectedReportName: selectedReport?.name,
    reportHeading
  });

  const renderReport = (categoryId) => {
    try {
      const propsData = {
        bridge: bridge,
        payerMixValue: payerMix,
        history: history,
        showOpenMyQueries: showOpenMyQueries
      }
      switch (categoryId) {
        case marketShareIDs.categoryId:
          return <MarketShare {...propsData} />
        case clinicalMarketShareIDs.categoryId:
          return <ClinicalMarketShare {...propsData} />
        case populationTrackerIDs.categoryId:
          return <PopulationTracker {...propsData} />
        case clinicalVolumesIDs.categoryId:
          return <ClinicalVolumes {...propsData} />
        case trendedPOSIDs.categoryId:
          return <TrendedPOS {...propsData} />
        case placeOfServiceByCountyIDs.categoryId: 
          return <PlaceOfServiceByCounty  {...propsData} />
        case placeOfServiceIDs.categoryId:
          return <PlaceOfService {...propsData} />
        case billingReportIDs.categoryId:
          return <BillingReport {...propsData} />
        case countyLevelServicesIDs.categoryId:
          return <CountyLevelServicesReport {...propsData} />
        case shareOfCareIDs.categoryId:
          return <ShareOfCare {...propsData} />
        case campaignEffectivenessIDs.categoryId:
          return <CampaignEffectiveness {...propsData} />
        default:
          return <div>Error finding report type. Please try again.</div>
      }
    }
    catch (error) {
      return <div>Error loading report. Please try again.</div>;
    }
  }

  return (
    <>
      <div className={`query-wrapper ${classes['query-detail-wrapper']}`}>
        <Breadcrumbs items={items} />
        <div className={classes['head-wrapper']}>
          <h3 className={classes['heading']}>Create a New Query</h3>
          <span className={classes['subtitle']}>Configure your data query parameters and preview results</span>
        </div>
        <div className={classes['detail-content-wrapper']}>
          <span className={classes['report-title']}>{selectedReport?.name}</span>
          <div className={classes['desc']}>{selectedReport?.description}</div>
          <div className="row">
            <div className="col-12">
              <Suspense fallback={<div>Loading Query Detail Page...</div>}>
                {renderReport(queryId)}
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default QueryDetails;
